import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Send, MessageSquare, UserPlus, Video } from 'lucide-react';
import { requestCall } from '../services/callService';
import { auth } from '../firebase-config';
import { displayAvatar, identiconDataUri } from '../services/identicon';
import { vibrate } from '../utils/vibration';
import {
  watchMyChats, watchReadMarks, watchMessages,
  sendChatMessage, markChatRead, ensureParticipants, getProfile,
} from '../services/chatV2Service';

/**
 * Chat v2 — the rebuilt guest chat surface (2026-08-31), running BESIDE the
 * legacy one while it's evaluated. Same Firestore data as legacy (see
 * chatV2Service), completely new UI: one component, two views (chat list ⇄
 * conversation), live last-50 messages, optimistic send, read watermarks,
 * unread dots — and none of the legacy surface's polling or timer chains.
 *
 * Deliberately self-contained: no message-channel hops into Jukebox4, no
 * cross-component command objects. Buttons pin their own styles inline
 * because ManufMuuzbox.scss paints every <button> with the logo gradient.
 */

const btnReset = {
  margin: 0,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
};

// WhatsApp-grade type scale (2026-08-31 user spec: "at least perform to the
// standards of WhatsApp"). Explicit px, NOT Tailwind text classes — the app's
// global styles rescale rem-based sizes on some surfaces, which is exactly
// why the chat read small.
const TYPE = {
  message: { fontSize: 17, lineHeight: 1.4 },
  name: { fontSize: 17 },
  preview: { fontSize: 16 },
  meta: { fontSize: 13 },
  headerName: { fontSize: 18 },
  input: { fontSize: 17, lineHeight: 1.4 },
};

const fmtTime = (ts) => {
  const ms = ts?.toMillis?.();
  if (!ms) return '';
  const d = new Date(ms);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString([], sameYear ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' });
};

const dayLabel = (ts) => {
  const ms = ts?.toMillis?.();
  if (!ms) return '';
  const d = new Date(ms);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const that = new Date(d); that.setHours(0, 0, 0, 0);
  const diff = Math.round((today - that) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long' });
};

const ChatV2 = ({ clientObj = null, onOpenMembers = null, initialChat = null }) => {
  const me = auth.currentUser;
  const myUid = me?.uid || null;

  const [chats, setChats] = useState(null);           // null = loading
  const [readMap, setReadMap] = useState({});
  const [active, setActive] = useState(initialChat);  // { chatId, users } | null
  const [messages, setMessages] = useState(null);     // null = loading
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [avatars, setAvatars] = useState({});         // uid -> photoURL|null
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const myName = me?.displayName || me?.email || 'Guest';

  // A conversation handed in from outside (Search Members → openChatById →
  // Jukebox4 passes it down) opens directly, including when it changes
  // after mount.
  useEffect(() => {
    if (initialChat?.chatId) setActive(initialChat);
  }, [initialChat?.chatId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── live data ─────────────────────────────────────────────────────────
  useEffect(() => watchMyChats(setChats), [myUid]);
  useEffect(() => watchReadMarks(setReadMap), [myUid]);
  useEffect(() => {
    if (!active?.chatId) { setMessages(null); return undefined; }
    ensureParticipants(active.chatId, active.users);
    return watchMessages(active.chatId, setMessages);
  }, [active?.chatId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Read watermark: stamp whenever the open conversation grows.
  useEffect(() => {
    if (!active?.chatId || messages === null) return;
    markChatRead(active.chatId, messages.length);
  }, [active?.chatId, messages?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── received-message haptics (~1s buzz, 2026-08-31 user spec) ─────────
  // Buzz ONLY for messages that ARRIVE — never on a chat's initial load,
  // never for your own sends. (Android vibrates; iOS Safari has no
  // Vibration API, so iPhones stay silent — a platform limit, not a bug.)
  // In the open conversation: watch the message count grow.
  const msgCountRef = useRef(null);
  useEffect(() => { msgCountRef.current = null; }, [active?.chatId]);
  useEffect(() => {
    if (messages === null) return;
    const prev = msgCountRef.current;
    msgCountRef.current = messages.length;
    if (prev === null || messages.length <= prev) return;
    const newest = messages[messages.length - 1];
    if (newest && newest.uid !== myUid) vibrate(1000);
  }, [messages, myUid]);
  // On the chat list (or with a different conversation open): watch every
  // OTHER chat's length advance with someone else as the last sender.
  const listStampRef = useRef(null);
  useEffect(() => {
    if (chats === null) return;
    const prev = listStampRef.current;
    listStampRef.current = new Map(chats.map((c) => [c.chatId, c.chatLength || 0]));
    if (prev === null) return; // first snapshot = history, not arrivals
    for (const c of chats) {
      if (c.chatId === active?.chatId) continue; // the open chat buzzes above
      if ((c.chatLength || 0) > (prev.get(c.chatId) ?? 0) && c.lastUser && c.lastUser !== myUid) {
        vibrate(1000);
        break; // one buzz per burst, not one per chat
      }
    }
  }, [chats, myUid, active?.chatId]);

  // Keep the newest message on screen.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages?.length, active?.chatId]);

  // Avatars for everyone visible, fetched once each (service caches too).
  const visibleUids = useMemo(() => {
    const set = new Set();
    (chats || []).forEach((c) => (c.users || []).forEach((u) => u?.id && set.add(u.id)));
    (active?.users || []).forEach((u) => u?.id && set.add(u.id));
    return [...set];
  }, [chats, active]);
  useEffect(() => {
    visibleUids.forEach((uid) => {
      if (avatars[uid] !== undefined) return;
      setAvatars((old) => ({ ...old, [uid]: null }));
      getProfile(uid).then((p) => {
        setAvatars((old) => ({ ...old, [uid]: p?.photoURL || null }));
      });
    });
  }, [visibleUids]); // eslint-disable-line react-hooks/exhaustive-deps

  const otherOf = (c) => (c.users || []).find((u) => u && u.id !== myUid) || null;
  const isUnread = (c) => {
    if (!c.lastUser || c.lastUser === myUid) return false;
    const mark = readMap[c.chatId];
    return mark === undefined || (c.chatLength || 0) > mark;
  };

  const send = async () => {
    const text = draft.trim();
    if (!text || sending || !active?.chatId) return;
    setSending(true);
    setDraft('');
    try {
      await sendChatMessage(active.chatId, {
        text,
        displayName: myName,
        clientId: clientObj?.clientId || null,
        users: active.users || [],
        newLength: (messages?.length || 0) + 1,
      });
    } catch (e) {
      setDraft(text); // give the words back rather than eating them
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const avatarFor = (uid, name) => displayAvatar(avatars[uid], uid ? uid.substring(0, 6) : name || '?');

  // ── conversation view ─────────────────────────────────────────────────
  if (active) {
    const other = otherOf(active) || {};
    let lastDay = '';
    return (
      <div className="flex flex-col text-white" style={{ height: '100%', minHeight: 0 }}>
        <div className="flex items-center gap-3 px-3 py-2.5 border-b border-white/10 flex-shrink-0">
          <button onClick={() => setActive(null)} aria-label="Back to chats"
            style={{ ...btnReset, padding: 8, color: '#E8EAF0', display: 'flex' }}>
            <ArrowLeft size={20} />
          </button>
          <img src={avatarFor(other.id, other.displayName)} alt=""
            className="w-9 h-9 rounded-full object-cover flex-shrink-0"
            onError={(ev) => { ev.currentTarget.src = identiconDataUri(other.id ? other.id.substring(0, 6) : '?'); }} />
          <div className="min-w-0 flex-1">
            <div className="font-semibold truncate" style={TYPE.headerName}>{other.displayName || 'Chat'}</div>
          </div>
          {other.id && (
            <button
              onClick={() => requestCall({
                chatId: active.chatId,
                calleeUid: other.id,
                calleeName: other.displayName || 'Guest',
              })}
              aria-label={`Video call ${other.displayName || ''}`}
              title="Start a video call"
              style={{
                margin: 0, border: 'none', cursor: 'pointer', flexShrink: 0,
                width: 42, height: 42, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'linear-gradient(135deg, #E8007D, #7C3AED)', color: '#fff',
              }}
            >
              <Video size={20} />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3" style={{ minHeight: 0 }}>
          {messages === null && <p className="text-white/40 text-center py-6" style={TYPE.message}>Loading…</p>}
          {messages !== null && messages.length === 0 && (
            <p className="text-white/40 text-center py-6" style={TYPE.message}>
              Say hello — this is the start of your conversation.
            </p>
          )}
          {(messages || []).map((m) => {
            const mine = m.uid === myUid;
            const day = dayLabel(m.createdAt);
            const showDay = day && day !== lastDay;
            lastDay = day || lastDay;
            return (
              <React.Fragment key={m.id}>
                {showDay && (
                  <div className="text-center my-3">
                    <span className="text-white/40 bg-white/5 border border-white/10 rounded-full px-3 py-1" style={{ fontSize: 14 }}>{day}</span>
                  </div>
                )}
                <div className={`flex mb-1.5 ${mine ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className="px-4 py-2.5"
                    style={{
                      ...TYPE.message,
                      maxWidth: '82%',
                      borderRadius: mine ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                      background: mine
                        ? 'linear-gradient(135deg, #E8007D, #7C3AED)'
                        : 'rgba(255,255,255,0.08)',
                      border: mine ? 'none' : '1px solid rgba(255,255,255,0.08)',
                      overflowWrap: 'anywhere',
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {m.text}
                    <span className="block text-right" style={{ ...TYPE.meta, opacity: 0.6, marginTop: 2 }}>
                      {m.createdAt ? fmtTime(m.createdAt) : '…'}
                    </span>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
          <div ref={bottomRef} />
        </div>

        <div className="flex items-end gap-2 px-3 py-2.5 border-t border-white/10 flex-shrink-0">
          <textarea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            rows={1}
            placeholder="Message…"
            className="flex-1 bg-white/5 border border-white/15 rounded-2xl px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/60 resize-none"
            style={{ ...TYPE.input, maxHeight: 120 }}
          />
          <button onClick={send} disabled={!draft.trim() || sending} aria-label="Send"
            style={{
              ...btnReset,
              width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: draft.trim() ? 'linear-gradient(135deg, #E8007D, #7C3AED)' : 'rgba(255,255,255,0.08)',
              color: '#fff', opacity: sending ? 0.6 : 1,
            }}>
            <Send size={19} />
          </button>
        </div>
      </div>
    );
  }

  // ── chat list view ────────────────────────────────────────────────────
  return (
    <div className="flex flex-col text-white" style={{ height: '100%', minHeight: 0 }}>
      <div className="flex-1 overflow-y-auto px-3 py-2" style={{ minHeight: 0 }}>
        {chats === null && <p className="text-white/40 text-center py-6" style={TYPE.message}>Loading your chats…</p>}
        {chats !== null && chats.length === 0 && (
          <div className="text-center py-10">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{ background: 'linear-gradient(135deg,#E8007D,#7C3AED)' }}>
              <MessageSquare size={26} className="text-white" />
            </div>
            <p className="text-base text-white/60 mb-4">No chats yet.</p>
            {onOpenMembers && (
              <button onClick={onOpenMembers}
                style={{ ...btnReset, padding: '12px 20px', borderRadius: 12, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#E8EAF0', fontSize: 16, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <UserPlus size={17} /> Find people in Search Members
              </button>
            )}
          </div>
        )}
        {(chats || []).map((c) => {
          const other = otherOf(c) || {};
          const unread = isUnread(c);
          return (
            <button key={c.chatId}
              onClick={() => setActive({ chatId: c.chatId, users: c.users || [] })}
              className="w-full flex items-center gap-3 text-left rounded-xl transition hover:bg-white/5"
              style={{ ...btnReset, padding: '10px 8px', width: '100%', color: '#E8EAF0' }}>
              <img src={avatarFor(other.id, other.displayName)} alt=""
                className="w-11 h-11 rounded-full object-cover flex-shrink-0"
                onError={(ev) => { ev.currentTarget.src = identiconDataUri(other.id ? other.id.substring(0, 6) : '?'); }} />
              <span className="flex-1 min-w-0">
                <span className={`block truncate ${unread ? 'font-bold' : 'font-medium'}`} style={TYPE.name}>
                  {other.displayName || 'Chat'}
                </span>
                <span className={`block truncate ${unread ? 'text-white/80' : 'text-white/45'}`} style={TYPE.preview}>
                  {c.lastUser === myUid ? 'You: ' : ''}{c.lastMessage || 'New chat'}
                </span>
              </span>
              <span className="flex flex-col items-end gap-1.5 flex-shrink-0">
                <span className="text-white/40" style={TYPE.meta}>{fmtTime(c.updatedAt)}</span>
                {unread && (
                  <span aria-label="Unread" style={{
                    width: 11, height: 11, borderRadius: '50%',
                    background: 'linear-gradient(135deg,#E8007D,#7C3AED)',
                  }} />
                )}
              </span>
            </button>
          );
        })}
      </div>
      {chats !== null && chats.length > 0 && onOpenMembers && (
        <div className="px-3 py-2.5 border-t border-white/10 flex-shrink-0">
          <button onClick={onOpenMembers}
            style={{ ...btnReset, padding: '10px 16px', borderRadius: 12, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#E8EAF0', fontSize: 16, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <UserPlus size={17} /> New chat
          </button>
        </div>
      )}
    </div>
  );
};

export default ChatV2;
